/**
 * A text cell shows its value as plain text. Click it twice, select it and
 * press Enter, or tap it again once selected, and it becomes an input in the same place, with the same type
 * and size, so nothing moves. Enter or Tab commits, Escape puts the old value
 * back, and leaving the cell commits too. Each commit is one save of one field.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Lock } from 'lucide-react';
import {
  type CellMove,
  dispatchCellMove,
  findGridRoot,
  focusGrid,
  focusIsWithGrid,
  isInActiveCell,
  onCellType,
  textCellProps,
} from '../dom';
import { CELL_CLASS } from './cellClass';

export type TextCellProps = {
  value: string;
  placeholder?: string;
  type?: 'text' | 'tel';
  disabled?: boolean;
  /**
   * Opens like any text cell, to read a long value or copy part of it, but
   * takes no edits; typing into it does nothing.
   */
  readOnly?: boolean;
  /**
   * Called with the new value when it differs from the old one, and with
   * where the cursor went when Enter or Tab committed it.
   */
  onCommit?: (value: string, move?: CellMove) => void;
};

export const TextCell: React.FC<TextCellProps> = ({
  value,
  placeholder,
  type = 'text',
  disabled,
  readOnly = false,
  onCommit,
}) => {
  const [editing, setEditing] = useState(false);
  // Only read while editing; each edit starts from the value or a typed key.
  const [draft, setDraft] = useState(value);
  const [refusedAt, setRefusedAt] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  // A phone has no double click or Enter: a tap on the selected cell edits.
  // Read on press, before that press selects the cell.
  const tapOnSelectedRef = useRef(false);

  const startEditing = (initial: string) => {
    setDraft(initial);
    setEditing(true);
  };

  // Typing on a selected cell replaces its value with what was typed, as in a
  // spreadsheet. The grid sends the key here as a `cell-type` event. The
  // display remounts after every edit, so the listener follows the element
  // through a callback ref rather than an effect.
  const stopListeningRef = useRef<(() => void) | null>(null);
  const displayRef = useCallback(
    (el: HTMLDivElement | null) => {
      stopListeningRef.current?.();
      stopListeningRef.current = null;
      if (!el || disabled || readOnly) return;
      stopListeningRef.current = onCellType(el, key => {
        setDraft(key);
        setEditing(true);
      });
    },
    [disabled, readOnly]
  );

  // Caret at the end, as when editing a spreadsheet cell, rather than the
  // whole value selected. A read-only cell opens to be copied, so there the
  // whole value is selected.
  useEffect(() => {
    const input = inputRef.current;
    if (editing && input) {
      if (readOnly) {
        input.select();
        return;
      }
      const end = input.value.length;
      input.setSelectionRange(end, end);
    }
  }, [editing, readOnly]);

  // When editing ends the input unmounts; give focus back to the grid so the
  // arrow keys keep working, as in a spreadsheet. Not when the edit ended
  // because the user clicked into something else, which keeps the focus.
  const refocusGrid = () => {
    const grid = findGridRoot(inputRef.current);
    requestAnimationFrame(() => {
      if (focusIsWithGrid(grid)) focusGrid(grid);
    });
  };
  const commit = (move?: CellMove) => {
    setEditing(false);
    refocusGrid();
    if (!readOnly && draft.trim() !== value.trim()) {
      onCommit?.(draft.trim(), move);
    }
  };
  const cancel = () => {
    setEditing(false);
    refocusGrid();
  };

  const onEditorKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // The grid's own keys must not see the editor's.
    e.stopPropagation();
    // Enter, Tab or Escape during an IME composition belongs to the IME (it
    // picks or drops a candidate); keyCode 229 marks the keydown that ends
    // one, which can arrive with isComposing already false.
    if (e.nativeEvent.isComposing || e.nativeEvent.keyCode === 229) return;
    // As in a spreadsheet: Enter keeps the value and moves down (Shift+Enter
    // up), Tab moves right (Shift+Tab left).
    const commitAndMove = (move: CellMove) => {
      e.preventDefault();
      dispatchCellMove(inputRef.current, move);
      commit(move);
    };
    if (readOnly && (e.key === 'Backspace' || e.key === 'Delete')) {
      // Nothing to delete in a read-only cell: it flashes.
      e.preventDefault();
      setRefusedAt(Date.now());
    } else if (e.key === 'Enter') {
      commitAndMove({ rows: e.shiftKey ? -1 : 1, cols: 0 });
    } else if (e.key === 'Escape') {
      cancel();
    } else if (e.key === 'Tab') {
      commitAndMove({ rows: 0, cols: e.shiftKey ? -1 : 1 });
    }
  };

  if (editing) {
    // As in a spreadsheet, a long value is not clipped while it is edited:
    // the editor floats over the cell, starts at the cell's width and grows
    // with its text across the cells beside it. The empty box underneath
    // keeps the row its height.
    return (
      <>
        <div className='h-9' />
        {/* The floating box: sized by its text, and where a refused key
            flashes, since it covers the cell. */}
        {/* One pixel past the cell's right and bottom, so the editor's border
            sits over the border the cell draws inside its own box. */}
        <div className='absolute left-0 top-0 z-20 flex min-w-[calc(100%+1px)] max-w-[32rem]'>
          <input
            ref={inputRef}
            type={type}
            value={draft}
            autoFocus
            readOnly={readOnly}
            style={{ fieldSizing: 'content' } as React.CSSProperties}
            className='box-border h-[calc(2.25rem+1px)] min-w-0 flex-auto rounded-none border-2 border-primary bg-white px-1.5 pb-px text-[13px] shadow-md outline-none'
            onChange={e => setDraft(e.target.value)}
            onBlur={() => commit()}
            onKeyDown={onEditorKeyDown}
          />
          {refusedAt > 0 && (
            <span
              key={refusedAt}
              aria-hidden
              className='refused-flash pointer-events-none absolute inset-0'
            />
          )}
        </div>
      </>
    );
  }

  return (
    <div
      ref={displayRef}
      {...textCellProps}
      role='button'
      // The grid owns keyboard focus and moves a cursor between cells.
      tabIndex={-1}
      aria-disabled={disabled}
      className={`${CELL_CLASS} ${disabled ? 'cursor-default' : 'cursor-text'}`}
      // A single click only selects the cell, as in a spreadsheet; a double
      // click edits. The grid's Enter key clicks this from code, which has no
      // click count (detail 0), so that edits too.
      onPointerDown={e => {
        tapOnSelectedRef.current =
          e.pointerType === 'touch' && isInActiveCell(e.currentTarget);
      }}
      onClick={e => {
        if (!disabled && (e.detail === 0 || tapOnSelectedRef.current)) {
          startEditing(value);
        }
      }}
      onDoubleClick={() => !disabled && startEditing(value)}
    >
      {value ? (
        <span className='min-w-0 flex-1 truncate'>{value}</span>
      ) : (
        <span className='min-w-0 flex-1 truncate text-muted-foreground/60'>
          {placeholder}
        </span>
      )}
      {readOnly && (
        <Lock
          aria-label='Cannot be changed here'
          className='ml-1 h-3 w-3 shrink-0 text-muted-foreground/50'
        />
      )}
    </div>
  );
};
